import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  /**
   * SNIP: adds that are already shown in the bubble but not yet confirmed by the server.
   * @type {Set<{quantity: number, shown: boolean}>}
   */
  #pendingAdds = new Set();

  /** @type {number | null} SNIP: the last cart count the server confirmed */
  #confirmedCount = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    // SNIP: when the event carries the cart, show the server count (plus adds still on their way)
    // instead of adding to whatever the bubble shows, so an optimistic add is never counted twice.
    const serverCount = event.detail.resource?.item_count;
    if (typeof serverCount === 'number') {
      this.#confirmedCount = serverCount;
      // Let other listeners (the drawer re-render) finish first, they also touch the bubble markup.
      await Promise.resolve();
      this.#renderExpectedCount(true);
      return;
    }

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
    this.#confirmedCount = this.currentCartCount - this.#shownPendingQuantity();
  };

  /**
   * SNIP: optimistic cart count. The caller shows the added quantity as soon as the product lands in the bag
   * and settles it once the server has answered; the server count then takes over.
   * @param {number} quantity - The quantity being added.
   * @returns {{ show: () => void, settle: (rerender?: boolean) => void }}
   */
  startOptimisticAdd(quantity) {
    if (this.#confirmedCount === null) this.#confirmedCount = this.currentCartCount || 0;

    const add = { quantity: Math.max(0, Number(quantity) || 0), shown: false };
    this.#pendingAdds.add(add);

    return {
      show: () => {
        if (!this.#pendingAdds.has(add) || add.shown) return;
        add.shown = true;
        this.#renderExpectedCount(true);
      },
      settle: (rerender = false) => {
        const wasShown = this.#pendingAdds.delete(add) && add.shown;
        if (rerender && wasShown) this.#renderExpectedCount(false);
      },
    };
  }

  /** @returns {number} */
  #shownPendingQuantity() {
    let quantity = 0;
    for (const add of this.#pendingAdds) {
      if (add.shown) quantity += add.quantity;
    }
    return quantity;
  }

  /**
   * Shows the confirmed count plus the adds that are already shown, and pops the bubble only when the number changes.
   * @param {boolean} animate
   */
  #renderExpectedCount(animate) {
    const count = (this.#confirmedCount ?? this.currentCartCount ?? 0) + this.#shownPendingQuantity();
    const changed = count !== this.currentCartCount || this.refs.cartBubbleCount.classList.contains('hidden') !== (count === 0);
    this.renderCartBubble(count, false, animate && changed);
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
          this.#confirmedCount = count;
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
